// Branches compare mode, Phase 2: sales performance of the selection against
// the benchmark (docs/decisions/branch-compare.md, "Phase 2 UI"). Computed
// from GET /api/branches/movements (one compact row per used unit) and, for
// the money cards, from the inventory units the page already loads.
//
// As in Phase 1 every branch is reduced to additive counts and sums (a
// "perf pool"); a pooled benchmark is a ratio of totals (the pooled median of
// days to sell is the median over the pooled sales), and the "Median branch"
// basis is the median of the per-branch figures (branches with n > 0).
// Everything is deterministic given the payload's `as_of`.

import {
    benchmarkFigure, benchmarkNames, compareDelta, median, MOS_DISPLAY_CAP, SMALL_SAMPLE_MIN,
} from './branchCompare';
import { isUsedUnit } from './branchView';
import { DEFAULT_SETTINGS } from './dealershipSettings';

/** Months in the window (the 12-month KPIs and the trend chart). */
export const PERF_MONTHS = 12;
/** Months of the "recent" pace in sales momentum. */
export const MOMENTUM_MONTHS = 3;
/** Look-back of the sell-through measure, in days. */
export const SELL_THROUGH_DAYS = 90;
/** Months of supply below this is Tight, above MOS_OVER is Overstocked. */
export const MOS_TIGHT = 2;
export const MOS_OVER = 6;

/**
 * "About the same" bands of the sales measures, in their own units: Similar
 * when the shown |difference| is under the band (the one rule of
 * compareDelta). Turns are shown to 0.1×, so the 0.2× band makes a shown
 * 0.1× Similar (QA B9).
 */
export const PERF_BANDS = Object.freeze({
    turns: 0.2, momentum: 2, medianDays: 3, sellThrough: 1, agedValue: 1,
});
/** Carrying cost per unit is Similar under this share of the benchmark (QA B4). */
export const PERF_CARRY_REL = 0.05;

// ── Dates (whole UTC days) ───────────────────────────────────────────────

const DAY_MS = 86400000;

/** 'YYYY-MM-DD' → whole days since 1970-01-01 (UTC); null when missing. */
export const dayOf = (iso) => {
    if (!iso) return null;
    const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
    if (!y || !m || !d) return null;
    return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
};

const dateOf = (day) => new Date(day * DAY_MS);

/** A day `months` calendar months from `day`, clamped to the month's end (31 Mar − 1 → 28/29 Feb). */
export const addMonths = (day, months) => {
    const dt = dateOf(day);
    const y = dt.getUTCFullYear();
    const m = dt.getUTCMonth() + months;
    const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    return Math.round(Date.UTC(y, m, Math.min(dt.getUTCDate(), last)) / DAY_MS);
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "Oct 22, 2026". */
export const formatDay = (day) => {
    const dt = dateOf(day);
    return `${MONTH_NAMES[dt.getUTCMonth()]} ${dt.getUTCDate()}, ${dt.getUTCFullYear()}`;
};

/**
 * The window from `as_of`: 13 boundaries b0..b12 one calendar month apart,
 * b12 = as_of, b0 = as_of − 12 months. Month k is the rolling month
 * (b_k, b_k+1], so the 12 months cover (b0, as_of] exactly and the last one
 * ends today (no partial calendar month). Month labels name the month each
 * one ends in; `ranges` are "Sep 23 – Oct 22, 2026".
 */
export const perfWindow = (asOfIso) => {
    const asOf = dayOf(asOfIso);
    const bounds = Array.from({ length: PERF_MONTHS + 1 }, (_, j) => addMonths(asOf, j - PERF_MONTHS));
    const labels = bounds.slice(1).map((d) => MONTH_NAMES[dateOf(d).getUTCMonth()]);
    const ranges = bounds.slice(1).map((end, k) => {
        const start = dateOf(bounds[k] + 1);
        return `${MONTH_NAMES[start.getUTCMonth()]} ${start.getUTCDate()} – ${formatDay(end)}`;
    });
    return {
        asOf, bounds, labels, ranges, d90: asOf - SELL_THROUGH_DAYS, cal: calendarWindow(asOf),
    };
};

/**
 * The sell-rate chart's months (QA B7): the last 12 complete calendar months
 * before the as-of month. `bounds` c0..c12 are the last day before each month
 * (c_k = 1st of month k − 1 day), so month k is (c_k, c_k+1] and its opening
 * stock is the stock at the start of the 1st (in stock on c_k). Labels are
 * month names, `years` the year on the first point and on January (else
 * null), `titles` "September 2026".
 */
export const calendarWindow = (asOf) => {
    const dt = dateOf(asOf);
    const y = dt.getUTCFullYear();
    const m = dt.getUTCMonth();
    const firsts = Array.from({ length: PERF_MONTHS + 1 }, (_, j) => Math.round(Date.UTC(y, m - PERF_MONTHS + j, 1) / DAY_MS));
    const months = firsts.slice(0, PERF_MONTHS).map(dateOf);
    return {
        bounds: firsts.map((d) => d - 1),
        labels: months.map((d) => MONTH_NAMES[d.getUTCMonth()]),
        years: months.map((d, k) => (k === 0 || d.getUTCMonth() === 0 ? String(d.getUTCFullYear()) : null)),
        titles: months.map((d) => `${MONTH_FULL[d.getUTCMonth()]} ${d.getUTCFullYear()}`),
    };
};

// ── Pools ────────────────────────────────────────────────────────────────

const zeros = (n) => Array.from({ length: n }, () => 0);

const emptyPerf = () => ({
    branches: 0,
    sales12: 0,
    sales3: 0,
    monthSales: zeros(PERF_MONTHS),
    stockAt: zeros(PERF_MONTHS + 1), // in stock on each boundary b0..b12
    calSales: zeros(PERF_MONTHS), // sales per calendar month (chart)
    calStockAt: zeros(PERF_MONTHS + 1), // in stock at the start of each calendar month (c0..c12)
    durations: [], // days to sell (>= 1) of the sales in the window
    stBase: 0, // in stock on as_of − 90 days
    stSold: 0, // … of which sold (sale_ok) by as_of
    stockNow: 0,
    // From the inventory (used units only)
    used: 0,
    priced: 0,
    askTotal: 0,
    agedAsk: 0,
    agedUnits: 0,
    carrySum: 0,
    carryN: 0,
});

/**
 * One branch's additive sales figures from its movements rows
 * ([branch_idx, category_idx|null, received, sold|null, sale_ok]). A unit is
 * in stock on day d when received ≤ d and (sold is null or sold > d). Sales
 * are rows with sale_ok = 1; same-day sales count as sales but not in the
 * days to sell (durations ≥ 1 day only).
 */
export const perfPool = (rows, win) => {
    const p = emptyPerf();
    p.branches = 1;
    const { bounds, asOf, d90 } = win;
    const cal = win.cal.bounds;
    const start = bounds[0];
    const recentFrom = bounds[PERF_MONTHS - MOMENTUM_MONTHS];
    (rows || []).forEach((r) => {
        const rec = dayOf(r[2]);
        if (rec == null) return;
        const sold = r[3] ? dayOf(r[3]) : null;
        const isSale = r[4] === 1 && sold != null;
        const inStockOn = (d) => rec <= d && (sold == null || sold > d);
        bounds.forEach((b, j) => { if (inStockOn(b)) p.stockAt[j] += 1; });
        cal.forEach((b, j) => { if (inStockOn(b)) p.calStockAt[j] += 1; });
        if (isSale && sold > cal[0] && sold <= cal[PERF_MONTHS]) {
            let k = 0;
            while (k < PERF_MONTHS - 1 && sold > cal[k + 1]) k += 1;
            p.calSales[k] += 1;
        }
        if (inStockOn(asOf)) p.stockNow += 1;
        if (inStockOn(d90)) {
            p.stBase += 1;
            if (isSale && sold <= asOf) p.stSold += 1;
        }
        if (isSale && sold > start && sold <= asOf) {
            p.sales12 += 1;
            if (sold > recentFrom) p.sales3 += 1;
            // The month (b_k, b_k+1] the sale falls in.
            let k = 0;
            while (k < PERF_MONTHS - 1 && sold > bounds[k + 1]) k += 1;
            p.monthSales[k] += 1;
            const days = sold - rec;
            if (days >= 1) p.durations.push(days);
        }
    });
    return p;
};

/**
 * Adds a branch's used inventory units to its pool: asking-price value and
 * the part older than the dealership's last aging boundary, carrying cost
 * so far (CarryingCostToDate) per used unit.
 */
export const addInventory = (pool, units, { aging = DEFAULT_SETTINGS.aging } = {}) => {
    const oldest = aging.boundaries[2];
    (units || []).filter(isUsedUnit).forEach((u) => {
        pool.used += 1;
        const ask = Number(u.Best_Sell_Price);
        if (Number.isFinite(ask) && ask > 0) {
            pool.priced += 1;
            pool.askTotal += ask;
            if ((Number(u.DaysInStock) || 0) > oldest) {
                pool.agedAsk += ask;
                pool.agedUnits += 1;
            }
        }
        const cc = u.CarryingCostToDate;
        if (typeof cc === 'number' && Number.isFinite(cc)) {
            pool.carrySum += cc;
            pool.carryN += 1;
        }
    });
    return pool;
};

/** The sum of several perf pools. */
export const addPerfPools = (pools) => pools.reduce((acc, p) => {
    Object.entries(p).forEach(([k, v]) => {
        if (typeof v === 'number') acc[k] += v;
        else if (k === 'durations') acc.durations = acc.durations.concat(v);
        else if (Array.isArray(v)) v.forEach((x, i) => { acc[k][i] += x; });
    });
    return acc;
}, emptyPerf());

/**
 * Per-branch perf pools, keyed by the /combined branch names (`allNames`):
 * movements rows by their branch name, inventory units by BranchName. A
 * branch with no movements gets an empty pool; movements of a branch that
 * /combined does not list are left out (the two lists match in practice).
 */
export const perfPoolsByBranch = (movements, allNames, invByBranch = null, settings = {}) => {
    const win = perfWindow(movements.as_of);
    const rowsBy = new Map(allNames.map((nm) => [nm, []]));
    (movements.units || []).forEach((r) => {
        const list = rowsBy.get(movements.branches[r[0]]);
        if (list) list.push(r);
    });
    const byName = new Map(allNames.map((nm) => {
        const p = perfPool(rowsBy.get(nm), win);
        if (invByBranch) addInventory(p, invByBranch[nm], settings);
        return [nm, p];
    }));
    return { byName, names: [...allNames], win };
};

/** The benchmark for a basis, as in branchCompare.benchmarkOf but over perf pools. */
export const perfBenchmarkOf = (basis, { byName, names }, selected, chosen = []) => {
    if (basis === 'median') {
        const all = names.map((n) => byName.get(n));
        return { kind: 'median', pools: all, branches: all.length };
    }
    const pooled = benchmarkNames(basis, names, selected, chosen).map((n) => byName.get(n));
    return { kind: 'pool', pool: addPerfPools(pooled), branches: pooled.length };
};

// ── Measures ─────────────────────────────────────────────────────────────

/** Mean of the 12 month-end stock counts (b1..b12). */
export const averageStock = (p) => p.stockAt.slice(1).reduce((a, b) => a + b, 0) / PERF_MONTHS;

/**
 * Months of supply → 'tight' | 'healthy' | 'over' (null without a figure),
 * decided on the value as shown (1 decimal, QA B5): under 2.0 Tight, 2.0 to
 * 6.0 (both included) Healthy, over 6.0 Overstocked.
 */
export const mosStatus = (months) => {
    if (months == null || !Number.isFinite(months)) return null;
    const shown = Number(months.toFixed(1));
    if (shown < MOS_TIGHT) return 'tight';
    if (shown > MOS_OVER) return 'over';
    return 'healthy';
};

export const MOS_STATUS = Object.freeze({
    tight: { word: 'Tight', tone: 'teal' },
    healthy: { word: 'Healthy', tone: 'ok' },
    over: { word: 'Overstocked', tone: 'warn' },
});

const monthsOfSupply = (stock, sales12) => (sales12 > 0 ? stock / (sales12 / PERF_MONTHS) : null);

/** "Selling faster than its 12-month pace" etc., from the momentum (%). */
export const paceText = (momentum, plural = false) => {
    if (momentum == null) return null;
    const its = plural ? 'their' : 'its';
    if (momentum >= PERF_BANDS.momentum) return `Selling faster than ${its} 12-month pace`;
    if (momentum <= -PERF_BANDS.momentum) return `Selling slower than ${its} 12-month pace`;
    return `Selling at ${its} 12-month pace`;
};

const fmtMoney = (v) => {
    const a = Math.abs(v);
    if (a >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
    if (a >= 1e3) return `$${Math.round(v / 1e3).toLocaleString('en-US')}k`;
    return `$${Math.round(v).toLocaleString('en-US')}`;
};
export { fmtMoney as formatMoneyShort };

/**
 * The measures, in reading order. `of(pool)` → { value, n }; `kind` sets the
 * format and the difference wording, `better` the good direction, `band` the
 * similar band, `nNoun` the n line's noun, `source` 'movements' or
 * 'inventory' (the money cards wait for the inventory).
 */
export const perfKpis = ({ aging = DEFAULT_SETTINGS.aging } = {}) => {
    const oldest = aging.boundaries[2];
    return [
        {
            id: 'turns',
            label: 'Used turns (12 months)',
            kind: 'turns',
            better: 'higher',
            band: PERF_BANDS.turns,
            nNoun: 'average used units in stock',
            source: 'movements',
            of: (p) => {
                const avg = averageStock(p);
                return { value: avg > 0 ? p.sales12 / avg : null, n: Math.round(avg) };
            },
            hint: 'Used units sold in the last 12 months divided by the average used stock (the mean of the 12 month-end stock counts). '
                + 'Higher is better: the same money in stock earns more sales.',
        },
        {
            id: 'momentum',
            label: 'Sales momentum',
            kind: 'momentum',
            better: 'higher',
            band: PERF_BANDS.momentum,
            nNoun: 'sales in 12 months',
            source: 'movements',
            of: (p) => ({
                value: p.sales12 > 0 ? ((p.sales3 / MOMENTUM_MONTHS) / (p.sales12 / PERF_MONTHS) - 1) * 100 : null,
                n: p.sales12,
            }),
            hint: 'Average monthly used sales in the last 3 months against the average month of the last 12 months. '
                + 'Above 0% the branch is selling faster than its usual pace, below 0% slower. Higher is better.',
        },
        {
            id: 'sellThrough',
            label: '90-day sell-through',
            kind: 'rate',
            better: 'higher',
            band: PERF_BANDS.sellThrough,
            nNoun: 'used units in stock 90 days ago',
            source: 'movements',
            of: (p) => ({ value: p.stBase > 0 ? (p.stSold / p.stBase) * 100 : null, n: p.stBase }),
            hint: 'Of the used units in stock 90 days ago, the share that has been sold since. Higher is better: older stock is moving.',
        },
        {
            id: 'medianDays',
            label: 'Median days to sell',
            kind: 'days',
            better: 'lower',
            band: PERF_BANDS.medianDays,
            daysPhrase: 'days (median)',
            nNoun: 'sales in 12 months (not same-day)',
            source: 'movements',
            of: (p) => ({ value: p.durations.length ? median(p.durations) : null, n: p.durations.length }),
            hint: 'The typical time from receiving a used unit to selling it, over the sales of the last 12 months: half sold faster, half slower. '
                + 'Units sold the day they arrived are left out. Lower is better.',
        },
        {
            id: 'mos',
            label: 'Months of supply',
            kind: 'months',
            better: 'band',
            nNoun: 'used units in stock',
            source: 'movements',
            of: (p) => ({
                value: monthsOfSupply(p.stockNow, p.sales12), n: p.stockNow, stock: p.stockNow, sales12: p.sales12,
            }),
            hint: `How many months today's used stock would last at the sales pace of the last 12 months. `
                + `Under ${MOS_TIGHT} months is Tight (you may run short), ${MOS_TIGHT} to ${MOS_OVER} is Healthy, over ${MOS_OVER} is Overstocked.`,
        },
        {
            id: 'agedValue',
            label: `Share of stock value older than ${oldest} days`,
            kind: 'rate',
            better: 'lower',
            band: PERF_BANDS.agedValue,
            nNoun: 'priced used units',
            source: 'inventory',
            of: (p) => ({ value: p.askTotal > 0 ? (p.agedAsk / p.askTotal) * 100 : null, n: p.priced, money: p.agedAsk }),
            hint: `Share of the used stock's asking-price value in units older than ${oldest} days, your oldest aging bucket. Lower is better: less money is tied up in old stock. `
                + `It is weighted by asking price, so it can differ from "Older than ${oldest} days" above, which counts units.`,
        },
        {
            id: 'carry',
            label: 'Carrying cost per unit',
            kind: 'money',
            better: 'lower',
            relBand: PERF_CARRY_REL,
            nNoun: 'used units in stock',
            source: 'inventory',
            of: (p) => ({ value: p.carryN > 0 ? p.carrySum / p.carryN : null, n: p.carryN, money: p.carrySum }),
            hint: 'Interest and holding cost built up so far on each used unit in stock, on average (your carrying cost settings). '
                + `Lower is better. A difference under ${PERF_CARRY_REL * 100}% of the benchmark counts as Similar.`,
        },
    ];
};

/** Measure groups, in reading order. */
export const PERF_GROUPS = [
    { title: 'Pace', ids: ['turns', 'momentum', 'sellThrough'] },
    { title: 'Time to sell', ids: ['medianDays'] },
    { title: 'Stock level', ids: ['mos'] },
    { title: 'Money tied up', ids: ['agedValue', 'carry'] },
];

/**
 * Months of supply against its healthy band: "Overstocked: 9.1 months vs
 * 4.2 dealership", "No sales in 12 months", or null without a figure.
 */
export const mosText = (sel, bench, benchShort, selSales = null) => {
    if (sel == null) return selSales === 0 ? 'No sales in 12 months' : null;
    const st = MOS_STATUS[mosStatus(sel)].word;
    const m = (v) => (v > MOS_DISPLAY_CAP ? `${MOS_DISPLAY_CAP}+` : v.toFixed(1));
    const b = bench == null ? 'no benchmark figure' : `${m(bench)} ${benchShort}`;
    return `${st}: ${m(sel)} months vs ${b}`;
};

/** The three statuses with their thresholds, for the visible legend. */
export const MOS_LEGEND = Object.freeze([
    { status: 'tight', text: `under ${MOS_TIGHT} months — may run short` },
    { status: 'healthy', text: `${MOS_TIGHT}–${MOS_OVER} months` },
    { status: 'over', text: `over ${MOS_OVER} months — more stock than the pace sells` },
]);

/** The verdict in plain words, per status. */
export const MOS_VERDICT = Object.freeze({
    tight: `Under ${MOS_TIGHT} months: at this pace the stock runs out within ${MOS_TIGHT} months — consider restocking.`,
    healthy: `Between ${MOS_TIGHT} and ${MOS_OVER} months: stock and sales pace are in balance.`,
    over: `Over ${MOS_OVER} months: at this pace it takes more than half a year to sell what's here.`,
});

const NO_PACE = 'no sales in 12 months, so the stock has no sell-through pace.';
const cap1 = (t) => t[0].toUpperCase() + t.slice(1);
const rateText = (r) => (r >= 0.1 ? r.toFixed(2) : String(Number(r.toPrecision(2))));
const monthsText = (v) => (v > MOS_DISPLAY_CAP ? `more than ${MOS_DISPLAY_CAP} months (shown as ${MOS_DISPLAY_CAP}+)` : `${v.toFixed(1)} months`);
const verdictText = (v) => (v > MOS_DISPLAY_CAP ? 'More than 2 years at this pace.' : MOS_VERDICT[mosStatus(v)]);

/**
 * The tooltip of a months-of-supply figure: the arithmetic with the precise
 * monthly rate (2 decimals, so 9 ÷ 0.92 = 9.8 adds up) and the verdict in
 * plain words. `fig` is { value, stock, sales12 } for a pooled figure or
 * { value, branches } for the median basis; `who` names the benchmark
 * ("The dealership") and is left out for the selection.
 */
export const mosFigureTip = (fig, { who = null, median: isMedian = false } = {}) => {
    const { value } = fig;
    if (isMedian) {
        if (value == null) return 'No branch has both used stock and sales in 12 months, so there is no median figure.';
        const k = fig.branches;
        const lead = k === 1 ? 'The only branch with used stock and sales' : `The middle branch of the ${k} branches with used stock and sales`;
        return `${lead}: ${monthsText(value)}. ${verdictText(value)}`;
    }
    const lead = who ? `${who}: ` : '';
    if (value == null) {
        const empty = 'no used units in stock and no sales in 12 months.';
        if (!fig.stock) return who ? `${lead}${empty}` : cap1(empty);
        return who ? `${lead}${fig.stock.toLocaleString('en-US')} in stock, but ${NO_PACE}` : cap1(NO_PACE);
    }
    const rate = fig.sales12 / PERF_MONTHS;
    return `${lead}${fig.stock.toLocaleString('en-US')} in stock ÷ ${rateText(rate)} sold per month = ${monthsText(value)}. ${verdictText(value)}`;
};

/**
 * Rows of the "Sales performance" panel, shaped like branchCompare's
 * buildCompareRows (sel, bench, n, range across branches, verdict, small
 * sample) plus: `status` / `benchStatus` / `statusText` for months of
 * supply (no Better / Worse verdict, so it is not counted in the takeaway),
 * `money` / `benchMoney` (the $ behind a share), `note` (the plain pace).
 * `benchWho` leads the benchmark's months of supply tip ("The dealership",
 * "Minot"; default "The {benchShort}").
 */
export const buildPerfRows = (selPool, bench, settings, branchPools = [], {
    benchShort = 'dealership', plural = false, benchWho = null,
} = {}) => (
    perfKpis(settings).map((kpi) => {
        const s = kpi.of(selPool);
        const b = benchmarkFigure(bench, kpi.of);
        const spread = branchPools.map(kpi.of).filter((r) => r.n > 0 && r.value != null).map((r) => r.value);
        const row = {
            id: kpi.id,
            label: kpi.label,
            kind: kpi.kind,
            better: kpi.better,
            band: kpi.band,
            relBand: kpi.relBand,
            hint: kpi.hint,
            source: kpi.source,
            daysPhrase: kpi.daysPhrase,
            sel: s.value,
            selN: s.n,
            nNoun: kpi.nNoun,
            bench: b.value,
            benchN: b.n,
            benchNoun: b.nNoun || kpi.nNoun,
            money: s.money ?? null,
            benchMoney: bench.kind === 'pool' ? (kpi.of(bench.pool).money ?? null) : null,
            rangeLo: spread.length ? Math.min(...spread) : null,
            rangeHi: spread.length ? Math.max(...spread) : null,
            small: s.n > 0 && s.n < SMALL_SAMPLE_MIN,
        };
        if (kpi.id === 'mos') {
            return {
                ...row,
                delta: null,
                tone: 'neutral',
                verdict: null,
                diffText: '—',
                status: mosStatus(s.value),
                benchStatus: mosStatus(b.value),
                statusText: mosText(s.value, b.value, benchShort, s.sales12),
                selTip: mosFigureTip(s),
                benchTip: bench.kind === 'median'
                    ? mosFigureTip({ value: b.value, branches: b.n }, { median: true })
                    : mosFigureTip(b, { who: benchWho || `The ${benchShort}` }),
                selText: s.value == null && s.sales12 === 0 ? 'No sales' : null,
            };
        }
        return {
            ...row,
            ...compareDelta(kpi, s.value, b.value),
            note: kpi.id === 'momentum' ? paceText(s.value, plural) : null,
        };
    })
);

/**
 * Sentence openings for the takeaway's biggest gaps (compareTakeaway
 * `openings`). Comparative, never directional, so they are true for any
 * signs (QA B1): "Sales momentum is weaker: +6.7% vs +19.4%".
 */
export const PERF_OPENINGS = Object.freeze({
    turns: (hi) => (hi ? 'Stock turns faster' : 'Stock turns slower'),
    momentum: (hi) => `Sales momentum is ${hi ? 'stronger' : 'weaker'}`,
    sellThrough: (hi) => `${hi ? 'More' : 'Less'} of the stock from 90 days ago has sold`,
    medianDays: (hi) => (hi ? 'Units take longer to sell' : 'Units sell sooner'),
    agedValue: (hi, o = {}) => `A ${hi ? 'larger' : 'smaller'} share of stock value is older than ${o.oldest ?? DEFAULT_SETTINGS.aging.boundaries[2]} days`,
    carry: (hi) => (hi ? 'Carrying cost per unit is higher' : 'Carrying cost per unit is lower'),
});

// ── Monthly sell rate (trend chart) ──────────────────────────────────────

/** Sell rate (%) of calendar month k: sales in the month ÷ stock at the start of its 1st. */
const rateOf = (p, k) => (p.calStockAt[k] > 0 ? (p.calSales[k] / p.calStockAt[k]) * 100 : null);

/**
 * The chart's two lines over the last 12 complete calendar months
 * (`win.cal`): the selection's monthly sell rate and the
 * benchmark's (pooled: total sales ÷ total opening stock; median: the median
 * of the branch rates of that month, branches with opening stock). Each point
 * carries its sales and opening stock (null for the median).
 */
export const sellRateSeries = (selPool, bench) => {
    const months = Array.from({ length: PERF_MONTHS }, (_, k) => k);
    const sel = months.map((k) => ({ rate: rateOf(selPool, k), sales: selPool.calSales[k], stock: selPool.calStockAt[k] }));
    const benchPts = months.map((k) => {
        if (bench.kind === 'median') {
            const rates = bench.pools.map((p) => rateOf(p, k)).filter((v) => v != null);
            return { rate: median(rates), sales: null, stock: null, branches: rates.length };
        }
        return { rate: rateOf(bench.pool, k), sales: bench.pool.calSales[k], stock: bench.pool.calStockAt[k] };
    });
    return { sel, bench: benchPts };
};
